import { useState } from "react";
import { FiPlus, FiUsers } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../../auth/AuthProvider";
import { API } from "../../api/api";

interface Customer {
  customer_id: string;
  customer_name: string;
  is_partner: boolean;
}

async function fetchCustomers(token: string): Promise<Customer[]> {
  const res = await fetch(`${API}/admin/customers`, {
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  });

  if (!res.ok) {
    const txt = await res.text();
    throw new Error(txt || `Failed to fetch customers: ${res.status}`);
  }
  return res.json();
}

export default function ClientsAll() {
  const nav = useNavigate();
  const queryClient = useQueryClient();
  const { token } = useAuth();

  const [isOpen, setIsOpen] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [isPartner, setIsPartner] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const {
    data,
    isLoading,
    isFetching,
    error,
  } = useQuery<Customer[]>({
    queryKey: ["customers"],
    queryFn: () => fetchCustomers(token || ""),
    enabled: !!token,
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 30,
    refetchOnWindowFocus: false,
  });

  const clients: Customer[] = data ?? [];

  const resetForm = () => {
    setName("");
    setEmail("");
    setIsPartner(false);
  };

  const handleCancel = () => {
    resetForm();
    setIsOpen(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);
    try {
      const res = await fetch(`${API}/admin/create-customer`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          customer_name: name,
          contact_email: email,
          is_partner: isPartner,
        }),
      });

      if (!res.ok) {
        const txt = await res.text();
        throw new Error(txt || `Create failed: ${res.status}`);
      }

      const created: Customer = await res.json();

      queryClient.setQueryData<Customer[]>(["customers"], (old: Customer[] | undefined) => [
        ...(old ?? []),
        created,
      ]);

      queryClient.invalidateQueries({ queryKey: ["customers"] });

      resetForm();
      setIsOpen(false);
      nav(`/admin/clients/${encodeURIComponent(created.customer_id)}`);
    } catch (err) {
      setSubmitError((err as Error).message ?? "Unknown error");
    }
  };

  return (
    <div className="p-6">
      {/* Page header */}
      <div className="mb-6">
        <h1 className="text-h2 text-text-primary">Customers</h1>
        <p className="text-body-sm text-text-secondary mt-1">
          Manage customer accounts and subscriptions
        </p>
      </div>

      {error && (
        <div className="mb-4 rounded-lg bg-health-500/10 border border-health-500/30 p-3 text-body-sm text-health-400">
          {(error as Error).message}
        </div>
      )}
      {submitError && (
        <div className="mb-4 rounded-lg bg-health-500/10 border border-health-500/30 p-3 text-body-sm text-health-400">
          {submitError}
        </div>
      )}

      {isLoading ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {[1, 2, 3].map(i => (
            <div key={i} className="h-32 bg-surface-700 animate-pulse rounded-xl" />
          ))}
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {clients.map((c) => (
            <div
              key={c.customer_id}
              onClick={() =>
                nav(`/admin/clients/${encodeURIComponent(c.customer_id)}`)
              }
              className="card flex h-32 cursor-pointer items-center justify-center gap-3 hover:bg-surface-600 transition-colors group"
            >
              <div className="p-2 rounded-lg bg-quantum-500/10 text-quantum-400 group-hover:bg-quantum-500/20">
                <FiUsers className="w-5 h-5" />
              </div>
              <span className="text-body text-text-primary font-medium">
                {c.customer_name}
              </span>
              {c.is_partner && (
                <span className="px-2 py-0.5 rounded-full text-caption font-medium bg-quantum-500/20 text-quantum-400">
                  Partner
                </span>
              )}
            </div>
          ))}

          {/* Create new tile */}
          <div
            onClick={() => setIsOpen(true)}
            className="flex h-32 cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed border-border bg-surface-800 text-text-muted hover:border-quantum-500/50 hover:bg-surface-700 transition-colors"
          >
            <FiPlus className="text-3xl text-quantum-400" />
            <span className="mt-2 text-body-sm font-medium">Create New Customer</span>
          </div>
        </div>
      )}

      {isFetching && !isLoading && (
        <p className="mt-2 text-caption italic text-text-muted">Updating list...</p>
      )}

      {/* Modal */}
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="w-full max-w-lg rounded-xl bg-surface-700 border border-border p-6 shadow-xl">
            <h3 className="mb-4 text-h4 text-text-primary">Add New Customer</h3>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="input-label">Name *</label>
                <input
                  required
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="input"
                  placeholder="Customer name"
                />
              </div>

              <div>
                <label className="input-label">Email *</label>
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="input"
                  placeholder="contact@example.com"
                />
              </div>

              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={isPartner}
                  onChange={(e) => setIsPartner(e.target.checked)}
                  className="w-4 h-4 rounded border-border bg-surface-800 text-quantum-500 focus:ring-quantum-500"
                />
                <span className="text-body-sm text-text-primary">Partner account</span>
                <span className="text-caption text-text-muted">
                  (can manage their own clients and projects)
                </span>
              </label>

              <div className="mt-6 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={handleCancel}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                >
                  Save Customer
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
